import { type PropsWithChildren, type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  comparePipelineStageOrder,
  isDefined,
  isEmpty,
  isNotDeleted,
  isNotEmpty,
  isWorkflowStageId,
  searchIncludes,
  splitArray,
  type WorkflowStage,
  type WorkflowStageId,
} from '@terros/common'
import {
  CloseButton,
  Combobox,
  type ComboboxProps,
  Group,
  Input,
  InputBase,
  type InputBaseProps,
  Text,
  Tooltip,
  useCombobox,
} from '@mantine/core'
import { MaterialSymbolIcon, WorkflowIconDisplay } from '../terros'
import {
  type CommonSelectProps,
  DisplaySelectOption,
  MultiSelectFilterOption,
  type MultiSelectFilterRowAction,
} from '../pickers'

type StageSelectProps = CommonSelectProps<WorkflowStageId> &
  Omit<ComboboxProps, 'children' | 'onOptionSubmit' | 'store'> & {
    stages: WorkflowStage[]
    inputProps?: InputBaseProps
    allowArchived?: boolean
    hideInitial?: boolean
    onAddNewStage?: (name: string) => void
  }

export const WorkflowStageSelect = ({
  stages,
  inputProps,
  allowArchived,
  hideInitial,
  onAddNewStage,
  ...props
}: StageSelectProps): ReactElement => {
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    return stages.filter((stage) => searchIncludes([JSON.stringify(stage.data)], search))
  }, [stages, search])

  const comboboxOptions = useMemo(() => {
    if (isEmpty(filtered)) return <Combobox.Empty>No stages found</Combobox.Empty>
    const options = []
    if (props.allowDeselect && isEmpty(search)) {
      options.push(
        <Combobox.Option key='none' value='none'>
          <DisplaySelectOption
            label={props.undefinedOptionLabel ?? 'No stage'}
            isSelected={props.value === undefined}
          />
        </Combobox.Option>
      )
    }
    options.push(
      getFilteredSelectOptions({
        filtered,
        allowArchived,
        hideInitial,
        isItemSelected: (stageId) => stageId === props.value,
      })
    )
    return options
  }, [props, filtered, allowArchived, search, hideInitial])

  const displayValue = useMemo(() => {
    if (props.allowDeselect && props.value === undefined) return props.undefinedOptionLabel ?? 'No stage'

    if (isDefined(props.value)) {
      const stage = stages.find((stage) => stage.id === props.value)
      if (stage)
        return (
          <Group gap='sm'>
            <WorkflowIconDisplay {...stage.displayProps} />
            {stage.name}
          </Group>
        )
      return '[Deleted Stage]'
    }
    return 'No Stage Selected'
  }, [props, stages])

  const handleOptionSelect = useCallback(
    (value: string) => {
      if (value === 'add') {
        if (isDefined(onAddNewStage) && isNotEmpty(search)) {
          onAddNewStage(search)
          setSearch('')
        }
        return
      }
      if (value === 'none') {
        if (!props.allowDeselect) return
        props.setValue(undefined)
        return
      }
      if (!isWorkflowStageId(value)) return
      props.setValue(value)
    },
    [props, onAddNewStage, search]
  )

  const handleToggleDropdown = (): void => {
    if (inputProps?.disabled) return
    combobox.toggleDropdown()
  }

  return (
    <Combobox width={300} position='bottom-start' {...props} store={combobox} onOptionSubmit={handleOptionSelect}>
      <Combobox.Target>
        <InputBase
          {...inputProps}
          onClick={handleToggleDropdown}
          pointer
          type='button'
          component='button'
          rightSection={
            props.allowDeselect && props.value ? (
              <CloseButton size='sm' onClick={() => props.setValue(undefined)} aria-label='Clear value' />
            ) : (
              <Combobox.Chevron />
            )
          }
        >
          {displayValue}
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder='Search stage'
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {comboboxOptions}
          {isNotEmpty(search) && isDefined(onAddNewStage) && <AddStageOption search={search} />}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export type FilteredSelectOptionsProps = {
  filtered: WorkflowStage[]
  allowArchived?: boolean
  hideInitial?: boolean
  isItemSelected: (stageId: WorkflowStageId) => boolean
  onItemToggle?: (stageId: WorkflowStageId) => void
  getItemRowAction?: (stageId: WorkflowStageId) => MultiSelectFilterRowAction
  doesItemToggleSelectAll?: (stageId: WorkflowStageId) => boolean
}
export const getFilteredSelectOptions = ({
  filtered: allStages,
  allowArchived,
  hideInitial,
  isItemSelected,
  onItemToggle,
  getItemRowAction,
  doesItemToggleSelectAll,
}: FilteredSelectOptionsProps): ReactNode => {
  const filtered = hideInitial ? allStages.filter((s) => !s.isInitial) : allStages
  const { yes: activeStages, no: archivedStages } = splitArray(filtered, isNotDeleted)
  const { yes: pipelineStages, no: otherStages } = splitArray(activeStages, (s) => s.isPipelineStage)
  pipelineStages.sort(comparePipelineStageOrder)

  const options = []
  if (isNotEmpty(pipelineStages)) {
    options.push(
      <Combobox.Group key='pipeline' label='Pipeline'>
        {pipelineStages.map((stage) => (
          <StageSelectOption
            key={stage.id}
            stage={stage}
            isSelected={isItemSelected(stage.id)}
            doesToggleSelectAll={doesItemToggleSelectAll?.(stage.id)}
            onToggle={onItemToggle}
            rowAction={getItemRowAction?.(stage.id)}
          />
        ))}
      </Combobox.Group>
    )
  }
  if (isNotEmpty(otherStages)) {
    options.push(
      <Combobox.Group key='other' label='Other'>
        {otherStages.map((stage) => (
          <StageSelectOption
            key={stage.id}
            stage={stage}
            isSelected={isItemSelected(stage.id)}
            doesToggleSelectAll={doesItemToggleSelectAll?.(stage.id)}
            onToggle={onItemToggle}
            rowAction={getItemRowAction?.(stage.id)}
          />
        ))}
      </Combobox.Group>
    )
  }
  if (isNotEmpty(archivedStages)) {
    options.push(
      <Combobox.Group key='archived' label='Archived'>
        {archivedStages.map((stage) => (
          <StageSelectOption
            key={stage.id}
            stage={stage}
            isSelected={isItemSelected(stage.id)}
            doesToggleSelectAll={doesItemToggleSelectAll?.(stage.id)}
            disabled={!allowArchived}
            onToggle={onItemToggle}
            rowAction={getItemRowAction?.(stage.id)}
          />
        ))}
      </Combobox.Group>
    )
  }
  return options
}

const StageSelectOption = ({
  stage,
  isSelected,
  doesToggleSelectAll,
  disabled,
  tooltip,
  onToggle,
  rowAction,
}: {
  stage: WorkflowStage
  isSelected: boolean
  doesToggleSelectAll?: boolean
  disabled?: boolean
  tooltip?: string
  onToggle?: (stageId: WorkflowStageId) => void
  rowAction?: MultiSelectFilterRowAction
}): ReactElement => {
  return (
    <OptionWrapper tooltip={tooltip}>
      {onToggle && rowAction ? (
        <MultiSelectFilterOption
          value={stage.id}
          disabled={disabled}
          label={stage.name}
          isSelected={isSelected}
          doesToggleSelectAll={doesToggleSelectAll ?? false}
          leftSection={<WorkflowIconDisplay {...stage.displayProps} />}
          onToggle={() => onToggle(stage.id)}
          rowAction={rowAction}
        />
      ) : (
        <Combobox.Option value={stage.id} disabled={disabled}>
          <DisplaySelectOption
            label={stage.name}
            isSelected={isSelected}
            leftSection={<WorkflowIconDisplay {...stage.displayProps} />}
          />
        </Combobox.Option>
      )}
    </OptionWrapper>
  )
}

const OptionWrapper = ({ children, tooltip }: PropsWithChildren<{ tooltip?: string }>): ReactNode => {
  if (isNotEmpty(tooltip)) {
    return <Tooltip label={tooltip}>{children}</Tooltip>
  }
  return children
}

export const AddStageOption = ({ search }: { search: string }): ReactElement => {
  return (
    <Combobox.Option value='add'>
      <Group align='center' gap='sm'>
        <MaterialSymbolIcon icon='add' />
        <Text size='sm'>
          Add new stage: <strong>{search}</strong>
        </Text>
      </Group>
    </Combobox.Option>
  )
}
